<div class="relative flex flex-col w-full min-h-full max-h-full">

  <!-- Header -->
  <div class="flex flex-0 items-center justify-between h-16 pr-3 sm:pr-5 pl-4 sm:pl-6 bg-gray-50 dark:bg-gray-900 border-b">
    <div class="flex items-center gap-3">
      <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
        <mat-icon class="icon-size-5 sm:icon-size-6 text-blue-600 dark:text-blue-400" [svgIcon]="'tabler_outline:refresh'"></mat-icon>
      </div>
      <div class="min-w-0">
        <h2 class="text-base sm:text-xl font-bold text-gray-900 dark:text-white truncate">Synchronizacja faktur</h2>
        <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">Pobierz faktury z KSeF za wybrany okres</p>
      </div>
    </div>
    <button mat-icon-button [matDialogClose]="undefined" class="flex-shrink-0">
      <mat-icon [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
    </button>
  </div>

  <!-- Content (scrollable) -->
  <div class="flex flex-auto overflow-hidden">
    <div class="flex flex-col flex-auto sm:mb-18 overflow-y-scroll sm:overflow-y-auto p-4 sm:p-6">

      <!-- Form -->
      <div *ngIf="!syncResult" class="space-y-4">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Wybierz zakres dat, z którego chcesz pobrać faktury. Zostaną zsynchronizowane zarówno faktury wystawione (przychody), jak i otrzymane (koszty).
        </p>

        <div class="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50">
          <mat-icon class="icon-size-4 text-blue-500 flex-shrink-0" [svgIcon]="'tabler_outline:info-circle'"></mat-icon>
          <p class="text-xs text-blue-700 dark:text-blue-400">Maksymalny zakres synchronizacji to {{ maxRangeDays }} dni.</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Data od</mat-label>
            <input matInput [matDatepicker]="pickerFrom" [formControl]="form.controls.dateFrom" [min]="minDate" [max]="maxDate">
            <mat-datepicker-toggle matIconSuffix [for]="pickerFrom"></mat-datepicker-toggle>
            <mat-datepicker #pickerFrom [touchUi]="isMobile"></mat-datepicker>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Data do</mat-label>
            <input matInput [matDatepicker]="pickerTo" [formControl]="form.controls.dateTo" [min]="minDate" [max]="maxDate">
            <mat-datepicker-toggle matIconSuffix [for]="pickerTo"></mat-datepicker-toggle>
            <mat-datepicker #pickerTo [touchUi]="isMobile"></mat-datepicker>
          </mat-form-field>
        </div>

        <!-- Range exceeded -->
        <div *ngIf="isRangeExceeded" class="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50">
          <div class="flex items-start gap-2">
            <mat-icon class="icon-size-5 text-amber-500 flex-shrink-0 mt-0.5" [svgIcon]="'tabler_outline:alert-triangle'"></mat-icon>
            <p class="text-sm text-amber-700 dark:text-amber-400">Wybrany zakres przekracza {{ maxRangeDays }} dni. Zmniejsz zakres dat, aby kontynuować.</p>
          </div>
        </div>

        <!-- Error -->
        <div *ngIf="syncError" class="p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50">
          <div class="flex items-start gap-2">
            <mat-icon class="icon-size-5 text-red-500 flex-shrink-0 mt-0.5" [svgIcon]="'tabler_outline:alert-triangle'"></mat-icon>
            <p class="text-sm text-red-700 dark:text-red-400">{{ syncError }}</p>
          </div>
        </div>
      </div>

      <!-- Syncing state -->
      <div *ngIf="isSyncing" class="flex flex-col items-center py-8 sm:py-12">
        <mat-spinner diameter="48"></mat-spinner>
        <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">Synchronizacja faktur z KSeF...</p>
        <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">To może potrwać kilka minut</p>
      </div>

      <!-- Result -->
      <div *ngIf="syncResult && !isSyncing" class="space-y-4">
        <div class="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50">
          <mat-icon class="icon-size-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" [svgIcon]="'tabler_outline:circle-check'"></mat-icon>
          <p class="text-sm font-medium text-emerald-700 dark:text-emerald-300">Synchronizacja zakończona pomyślnie</p>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <!-- Przychody -->
          <div class="p-3 sm:p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Przychody</p>
            <p class="font-semibold text-gray-900 dark:text-white mt-1">
              +{{ syncResult.income?.inserted || 0 }} nowych
            </p>
            <p *ngIf="syncResult.income?.updated" class="text-xs text-gray-500">
              {{ syncResult.income.updated }} zaktualizowanych
            </p>
          </div>

          <!-- Koszty -->
          <div class="p-3 sm:p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <p class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Koszty</p>
            <p class="font-semibold text-gray-900 dark:text-white mt-1">
              +{{ syncResult.expense?.inserted || 0 }} nowych
            </p>
            <p *ngIf="syncResult.expense?.updated" class="text-xs text-gray-500">
              {{ syncResult.expense.updated }} zaktualizowanych
            </p>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- Footer (sticky at bottom) -->
  <div class="dialog-footer absolute inset-x-0 bottom-0 py-3 sm:py-4 px-4 sm:px-6 border-t z-10 bg-gray-50 dark:bg-gray-900">
    <div class="flex items-center justify-end gap-3">
      <button mat-stroked-button (click)="close()" class="dialog-btn">
        {{ syncResult ? 'Zamknij' : 'Anuluj' }}
      </button>
      <button *ngIf="!syncResult"
              mat-flat-button
              color="primary"
              class="dialog-btn"
              [disabled]="isSyncing || !form.controls.dateFrom.value || !form.controls.dateTo.value || isRangeExceeded"
              (click)="startSync()">
        <mat-icon *ngIf="!isSyncing" class="icon-size-4" [svgIcon]="'tabler_outline:refresh'"></mat-icon>
        <span class="ml-2">{{ isSyncing ? 'Synchronizowanie...' : 'Synchronizuj' }}</span>
      </button>
    </div>
  </div>

</div>
